/* eslint react-hooks/exhaustive-deps: "warn" */

import { useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import styles from './MaActivityUseCasesSection.module.scss';

type Translate = (key: string) => string;

const getRows = (t: Translate) => [
  {
    num: '01',
    tag: t('Market mapping'),
    for: 'IB · Advisory · Consulting',
    line: (
      <>
        {t('Map the whole sector — including the companies that')}{' '}
        <span className={styles.accent}>{t('never raised.')}</span>
      </>
    ),
    sub: t('Filed revenue, ownership, and deal history behind every name.'),
  },
  {
    num: '02',
    tag: t('Deal sourcing'),
    for: 'PE · Corp Dev · VC',
    line: (
      <>
        {t('Find the founder-owned target')} <span className={styles.accent}>{t('before the banks do.')}</span>
      </>
    ),
    sub: t('Registry signals — ownership, filed growth, succession — across 600M+ companies.'),
  },
  {
    num: '03',
    tag: t('Commercial due diligence'),
    for: 'Deal teams · Consulting',
    line: (
      <>
        {t('Validate the market on')} <span className={styles.accent}>{t('filed numbers,')}</span> {t('not estimates.')}
      </>
    ),
    sub: t('The statements of every competitor, customer, and supplier around the target.'),
  },
];

export const MaActivityUseCasesSection = () => {
  const { t } = useInt();
  const ref = useRef<HTMLElement>(null);
  const inView = useIntersection(ref, { threshold: 0.15 });
  const rows = getRows(t);

  return (
    <section className={styles.section} ref={ref}>
      <div className={styles.container}>
        {rows.map((row, i) => (
          <div
            key={row.num}
            className={cn(styles.row, inView && styles.rowVisible)}
            style={{ '--delay': `${i * 0.12}s` } as React.CSSProperties}
          >
            <div className={styles.meta}>
              <span className={styles.num}>{row.num}</span>
              <span className={styles.tag}>{row.tag}</span>
              <span className={styles.forLabel}>{row.for}</span>
            </div>
            <h3 className={styles.line}>{row.line}</h3>
            <p className={styles.sub}>{row.sub}</p>
          </div>
        ))}
      </div>
    </section>
  );
};
